import styled from '@emotion/styled'
import { useEffect, useState } from 'react'

import { ApiAccountApprovalEvent } from '../../../../../api/_legacy/generated-api'
import {
  CommitModal,
  SimpleTabStrip,
  Space,
  Typography,
  ValidationMessage
} from '../../../../../components/core-v2'
import { StepTab } from '../../../../../components/core-v2/molecules/step-tab/step-tab'
import { TabContent } from '../../../../../components/core-v2/molecules/tab-content'
import { TradeApprove } from '../../../../../components/core-v2/molecules/trade-approve'
import TradeApproveReadonly from '../../../../../components/core-v2/molecules/trade-approve-readonly'
import TradeDocuments from '../../../../../components/core-v2/organisms/trade-documents'
import TradeEventDisplay from '../../../../../components/core-v2/organisms/trade-event-display/trade-event-display'
import { TradeActivationStep } from '../../../../tradebuilder-v2/types'
import { formatDateAsString } from '../../../../tradebuilder-v2/util'
import { DESCRIPTIONS } from '../../../constants'
import { TradeCommitmentStepState } from '../state/trade-commitment-slice'

export type TradeCommitmentStatelessProps = {
  displayClientCommit: boolean
  onBusinessCommit: () => void
  onClientCommit: () => void
  onCloseConfirmCommit: () => void
  onRequestConfirmCommit: () => void
  onSetAuthorised: (isAuthorised: boolean) => void
  onTradeReject: (comments: string) => void
  state: TradeCommitmentStepState
}

const StyledDownloadWrapper = styled.div`
  div {
    background: var(--White) !important;
  }
`

const TradeCommitmentStateless = ({
  displayClientCommit,
  onBusinessCommit,
  onClientCommit,
  onCloseConfirmCommit,
  onRequestConfirmCommit,
  onSetAuthorised,
  onTradeReject,
  state
}: TradeCommitmentStatelessProps) => {
  const [selectedStep, setSelectedStep] = useState<TradeActivationStep>(undefined)
  const [completedStep, setCompletedStep] = useState<TradeActivationStep>(undefined)

  const isBusinessEditable =
    state.userType === 'admin' ||
    (state.userType === 'business-unit' && state.userRoles.includes('Editor')) ||
    (state.userType === 'absa' &&
      (state.userRoles.includes('BankAdmin') || state.userRoles.includes('Sales')))
  const isClientEditable =
    state.userType === 'admin' ||
    state.userType === 'client' ||
    (state.userType === 'absa' && state.userRoles.includes('BankAdmin'))

  const cutOffTimeStr = formatDateAsString(new Date(state.cutOffTime))

  useEffect(() => {
    const currentStep = state.status
    if (currentStep > TradeActivationStep.clientCommitment) {
      setSelectedStep(TradeActivationStep.commitment)
    } else {
      setSelectedStep(currentStep)
    }
    setCompletedStep(currentStep)
  }, [state.status])

  const getState = (currentTabPosition: number) => {
    if (currentTabPosition === completedStep) return 'current'
    const isCompleted = currentTabPosition <= completedStep
    return isCompleted ? 'complete' : 'pending'
  }
  const renderStepTab = (
    stepNumber: number,
    tradeActivationStep: TradeActivationStep,
    stepName: string
  ) => (
    <StepTab
      isSelected={selectedStep === tradeActivationStep}
      state={getState(tradeActivationStep)}
      stepNumber={stepNumber}
      onClick={() => setSelectedStep(tradeActivationStep)}
    >
      {stepName}
    </StepTab>
  )

  const renderReadOnly = (clientOrBusinessName: string) => (
    <Space my={6}>
      <TradeApproveReadonly
        waitingDescription={`This trade is awaiting commitment from ${clientOrBusinessName}. Please check back later`}
      />
    </Space>
  )

  const renderTab = (
    onCommit: () => void,
    onSetAuthorised: (isAuthorised: boolean) => void,
    hasCompleted: boolean,
    isEditable: boolean,
    clientOrBusinessName: string,
    inScopeApprovalEvents: ApiAccountApprovalEvent[],
    onRequestConfirmCommit?: () => void
  ) => (
    <>
      {hasCompleted &&
        inScopeApprovalEvents.map((approvalEvent, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <TradeEventDisplay key={index.toString()} {...approvalEvent} />
        ))}
      {!hasCompleted && isEditable && (
        <>
          <Space my={6}>
            <Space mb={5}>
              <Typography variant="b3">{DESCRIPTIONS.TRADE_DOCUMENTS_APPROVE}</Typography>
            </Space>
            <StyledDownloadWrapper>
              <TradeDocuments tradeDocumentationUrl={state.tradeDocumentationUrl} />
            </StyledDownloadWrapper>
          </Space>
          <Space mt={5}>
            <ValidationMessage errors={state.errors} />
          </Space>
          <Space mb={5}>
            Commitment must be provided no later than {cutOffTimeStr}, otherwise the trade will be
            rejected and will need to be resubmitted.
          </Space>
          <TradeApprove
            canApprove
            allowComments={false}
            approveBtnCaption="Commit to the Trade"
            approvingSection="trade"
            userName={state.userName}
            onApprove={onRequestConfirmCommit || onCommit}
            onReject={onTradeReject}
          />
          {state.isConfirmingCommit && (
            <CommitModal
              isAuthorised={state.isConfirmedAuthorised}
              onClose={onCloseConfirmCommit}
              onCommit={onCommit}
              onSetAuthorisation={onSetAuthorised}
            />
          )}
        </>
      )}
      {!hasCompleted && !isEditable && renderReadOnly(clientOrBusinessName)}
    </>
  )

  const StyledTabs = styled.div`
    border-bottom: solid 1px var(--White90);
  `

  return (
    <Space mt={6}>
      <StyledTabs>
        <SimpleTabStrip>
          {renderStepTab(1, TradeActivationStep.commitment, `${state.businessUnitName} commitment`)}
          {displayClientCommit &&
            renderStepTab(
              2,
              TradeActivationStep.clientCommitment,
              `${state.clientName} commitment`
            )}
        </SimpleTabStrip>
      </StyledTabs>
      <TabContent>
        {selectedStep === TradeActivationStep.commitment &&
          renderTab(
            onBusinessCommit,
            onSetAuthorised,
            state.hasBusinessCompleted,
            isBusinessEditable,
            state.businessUnitName,
            state.approvalEvents,
            onRequestConfirmCommit
          )}
        {selectedStep === TradeActivationStep.clientCommitment &&
          renderTab(
            onClientCommit,
            onSetAuthorised,
            state.hasClientCompleted,
            isClientEditable,
            state.clientName,
            state.clientApprovalEvents
          )}
      </TabContent>
    </Space>
  )
}

export default TradeCommitmentStateless
